import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Crown, Check, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        refreshSubscription();
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Status check error:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-lg">Confirming your subscription...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          {/* Success Animation */}
          <div className="relative mb-6">
            <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-6 rounded-full w-24 h-24 mx-auto flex items-center justify-center">
              <Crown className="h-12 w-12 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-green-500 p-2 rounded-full">
              <Check className="h-4 w-4 text-white" />
            </div>
          </div>

          <h1 className="text-3xl font-serif font-bold mb-4">Welcome to Premium!</h1>
          <p className="text-slate-600 mb-8">
            Your subscription is now active. Enjoy unlimited access to all articles and exclusive features.
          </p>

          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
            >
              Start Reading <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              onClick={() => navigate('/settings')}
              variant="outline"
              className="w-full"
            >
              Set Up Email Alerts
            </Button>
            <Button 
              onClick={() => navigate('/referral')}
              variant="ghost"
              className="w-full"
            >
              Refer Friends & Earn Free Months
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Failed or timeout
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <h1 className="text-2xl font-bold mb-4">
          {status === 'timeout' ? 'Taking longer than expected' : 'Something went wrong'}
        </h1>
        <p className="text-slate-600 mb-6">
          {status === 'timeout' 
            ? 'Your payment may still be processing. Check your email for confirmation.'
            : 'The payment could not be completed. Please try again.'}
        </p>
        <Button onClick={() => navigate('/subscribe')}>
          Back to Subscription Page
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
